<template>
  <!--
   This component has 3 different uses (as of 12/14/21):
     1. To add products to a product row (/components/ProductRowComponent.vue)
     2. To select a single image for the Add Image modal (/components/ImageBoxComponent.vue)
     3. To select multiple images for a collage (/components/ImageBox/AddImageImageBoxTool.vue)
  -->
  <div>
    <base-modal
      id="product-selector"
      class="modal-selector"
      :class="isIPad ? 'keyboardUp' : ''"
      :show-footer="true"
      :show="show"
      :size="isIPad ? 'modal-md' : tabSize"
      style="padding-left : 16px; padding-right : 16px"
      @closed="onClosed()"
    >
      <div class="heading">
        <div class="align-items-start px-3 py-3">
          <!-- <base-heading :level="2">
            {{ $t('common.add') }} {{ feature }}
          </base-heading> -->
          <base-card-title expanded>
            <!-- {{ $t('common.add') }} {{ selectableMode === UPLOAD_MODE.CATALOG ? 'Product' : feature }} --> <!-- e.g.  "Add Image" -->
            Add Product
          </base-card-title>
          <base-tabs
            v-model="tab"
            class="tab-fixes justify-content-start bottom-line"
            :items="tabs.map((tab) => tab.name)"
            underlined
            bold
            @tabChanged="changeTab"
          />
        </div>
      </div>
      <image-uploader64
        v-show="selectableMode === UPLOAD_MODE.UPLOAD"
        :key="`upload-${selectorKey}`"
        ref="imgUploader"
        v-model="uploadSelected"
        :multiple="multiselect"
      />
      <product-selector-fetch
        v-show="selectableMode === UPLOAD_MODE.FETCH"
        :key="`fetch-${selectorKey}`"
        ref="productSelectorFetch"
        v-model="fetchSelected"
        :disable-multi-select="disableMultiFetchSelect"
        :show-shared-collages="showSharedCollages"
        @submitted="tab = 0"
        @fetched="hasImagesToSelect = $event"
      />
      <collateral-selector
        v-show="selectableMode === UPLOAD_MODE.ALBUM"
        :key="`collateral-${selectorKey}`"
        v-model="collateralSelected"
        :images="elements"
        :multiple="multiselect"
        @load-more-images="onLoadMoreImages"
        @folder-changed="onFolderChanged"
        @search="onSearch"
      />

      <div v-if="selectableMode === UPLOAD_MODE.CATALOG">
        <catalog-product-selector :key="`catalog-${selectorKey}`" style="margin-left: 15px; height: 495px" />
      </div>

      <template slot="footer">
        <div class="w-100 d-flex justify-content-end mt-5">
          <div>
            <template v-if="productImageStatus === 'idle' || productImageStatus === 'error'">
              <base-button
                v-if="hasImagesToSelect || selectableMode !== UPLOAD_MODE.FETCH"
                inverse
                :disabled="selected.length === 0"
                class="add-image-button"
                @click="onAddImage"
              >
                <!-- {{ uploadSelected.length <= 1 ? $t('outreach.product.addImage'): $t('outreach.product.addImages') }} {{ selectItem ? selectItem : '' }} -->
                Add {{ uploadSelected.length > 0 ? `(${uploadSelected.length})` : '' }}{{ selectItem > 0 ? `(${selectItem})` : '' }}
              </base-button>
            </template>
            <template v-if="productImageStatus === 'loading'">
              <base-button
                inverse
                :disabled="true"
              >
                <font-awesome-icon
                  icon="spinner"
                  class="mr-1"
                  spin
                />
                {{ $t('outreach.product.creating') }}
              </base-button>
            </template>
          </div>
        </div>
      </template>
    </base-modal>
  </div>
</template>
<script>
import _ from 'lodash';
import Listable from '@/components/mixins/listable';
import productImageUtil from '@/_utilities/productImage';
import CatalogProductSelector from '@/components/Widgets/Catalog/CatalogProductSelector';
import { sendToMixPanel } from '@/_utilities/seerMixPanel';
import whichbrowser from '@/components/mixins/whichbrowser';
import { EventBus } from '@/event-bus';
import productSelectorFetch from './ProductSelectorFetch';
import { MIXPANEL_EVENTS } from '../_utilities/Enums';

export const UPLOAD_MODE = {
  FETCH: 'fetch',
  UPLOAD: 'upload',
  ALBUM: 'album',
  CATALOG: 'catalog',
};

export default {
  name: 'ProductSelector',
  components: {
    productSelectorFetch,
    CatalogProductSelector,
  },
  mixins: [Listable, whichbrowser],
  props: {
    location: {
      type: String,
      default: '',
    },
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Select products',
    },
    uploader: {
      type: Boolean,
      default: false,
    },
    useStore: {
      type: Boolean,
      default: false,
    },
    disableUrlUpload: {
      type: Boolean,
      default: false,
    },
    disableComputerUpload: {
      type: Boolean,
      default: false,
    },
    disableCollateralUpload: {
      type: Boolean,
      default: false,
    },
    disableCatalogUpload: {
      type: Boolean,
      default: false,
    },
    disableMultiSelect: {
      // disables ability to select multiple collaterals or upload multiple images.
      type: Boolean,
      default: false,
    },
    /*
      disableMultiFetchSelect disables ability to select multiple fetched
      images in 'fetch' mode (from URL)

      This used to be the same as disableMultiSelect, but since adding support
      for bulk creation of product images from albums to product rows, they
      drifted apart:
          1. in ProductRowComponent you can add multiple collaterals but only
             select a single fetched image.

          2. in Collages (AddImageImageBoxTool) multiple collaterals can be
             selected, AND multiple fetched images.

          3. in Add Image (ImageBoxComponent) you can only select a single
             collateral, or a single fetched image.
     */
    disableMultiFetchSelect: {
      type: Boolean,
      default: false,
    },
    feature: {
      type: String,
      default: 'Image',
    },
    showSharedCollages: {
      type: Boolean,
      default: true,
    },
    noMultiRequest: { // Only last fetch is valid.
      type: Boolean,
      default: true,
    },

    // -- listable.js props --
    source: {
      type: String,
      default: 'collateral',
    },
    pageSize: {
      type: Number,
      default: 60,
    },
    mode: {
      type: String,
      default: 'loadMore',
    },
    selectItem: {
      type: Number,
      default: 0,
    },

  },
  data() {
    return {
      fetchSelected: [],
      collateralSelected: [],
      uploadSelected: [],
      hasImagesToSelect: false,
      tab: 0,
      selectorKey: Date.now(),
      shared: false,
      sync_target: null,

      // -- listable.js props --
      queryParams: {
        fromProductSelector: true,
      },
    };
  },
  computed: {
    selected() { // XXX see 'selectionRecord' in select() at src/components/ProductSelectorFetch.vue:248
      return this.tabs[this.tab].vmodel;
    },
    currentTab() {
      return this.tabs[this.tab];
    },
    tabs() {
      const alltabs = [
        {
          name: this.$t('outreach.product.fromURL'),
          selectableMode: UPLOAD_MODE.FETCH,
          vmodel: this.fetchSelected,
          visible: !this.disableUrlUpload,
          size: 'modal-xl',
        },
        {
          name: this.$t('outreach.product.upload'),
          selectableMode: UPLOAD_MODE.UPLOAD,
          vmodel: this.uploadSelected.length ? this.uploadSelected.map((e) => ({ image_url: e.src })) : [],
          visible: !this.disableComputerUpload,
          size: 'modal-xl',
        },
        {
          name: this.$t('navbar.assets'),
          selectableMode: UPLOAD_MODE.ALBUM,
          vmodel: this.collateralSelected,
          visible: !this.disableCollateralUpload,
          size: 'modal-xl',
        },
        {
          name: this.$t('navbar.catalog'),
          selectableMode: UPLOAD_MODE.CATALOG,
          vmodel: this.selectedProducts,
          visible: !this.disableCatalogUpload && this.useFeature('product_catalog'),
          size: 'modal-xl',
        },
      ];
      return alltabs.filter((tab) => tab.visible);
    },
    productImageStatus() {
      return this.$store.state.productImage.status;
    },
    selectableMode() {
      return this.tabs[this.tab].selectableMode;
    },
    itemsCount() {
      return this.$store.state.collateral.count;
    },
    fetchRoute() {
      return this.shared ? `${this.source}/getSharedCollages` : Listable.computed.fetchRoute.call(this);
    },
    multiselect() {
      if (this.shared || this.disableMultiSelect) {
        return false;
      } else {
        return true;
      }
    },
    selectedProducts() {
      return this.$store.getters['catalog/selectedProducts'];
    },
    tabSize() {
      return this.currentTab.size ?? 'modal-lg';
    },
    parsingRequired() {
      return [UPLOAD_MODE.ALBUM, UPLOAD_MODE.CATALOG].includes(this.selectableMode);
    },
  },
  watch: {
    show(newVal) {
      if (!newVal) {
        this.resetSelect();
      }
    },
    search(newval) {
      const data = {
        folder_pk: this.selectedFolderPk,
        search: newval,
      };
      this.$emit('search', data);
    },
  },
  mounted() {
    EventBus.$on('selectedItem', (val) => {
      this.selectItem = val;
    });
  },
  created() {
    // Exposing const to the template
    this.UPLOAD_MODE = UPLOAD_MODE;
  },
  methods: {
    /** sets folder pk on queryParams.folder if it's not the "All" folder, else cleans the queryparam */
    hasFolderParam(folder) {
      if (folder && !folder.all && this.queryParams.folder !== -1) {
        this.queryParams.folder = folder.pk;
      } else {
        delete this.queryParams.folder;
      }
    },
    onFolderChanged(folder) {
      this.sync_target = folder.sync_target;
      if (folder.shared !== undefined && folder.shared) {
        this.shared = true;
        this.truePage = 1;
        this.reset();
      } else {
        this.hasFolderParam(folder);
        this.reset();
        this.truePage = 1;
        this.shared = false;
      }
      this.fetchData();
    },
    /**
     * We are checking if this file comes from a synced folder.
     */
    hasMetadata(selected) {
      const metadataIsValid = true;
      if (selected.meta !== null && selected.meta !== undefined) {
        try {
          const meta = JSON.parse(selected.meta);
          if ('site_url' in meta && 'external_id' in meta) {
            return metadataIsValid;
          }
        } catch {
          return !metadataIsValid;
        }
      }
      return !metadataIsValid;
    },
    onClosed() {
      EventBus.$emit('closeModalSelector', false);
      this.$emit('cancel');
    },
    changeTab(index) {
      this.tab = index;
    },
    resetSelect() {
      this.fetchSelected = [];
      this.collateralSelected = [];
      this.uploadSelected = [];

      if (this.$refs.imgUploader) {
        this.$refs.imgUploader.reset();
      }
      this.changeTab(0);
      this.selectorKey = Date.now();
      // this.search = '';
    },
    parseCollateralToProductImage(collateral) {
      if (this.hasMetadata(collateral)) {
        const collateralMeta = JSON.parse(collateral.meta) || {};
        return productImageUtil.create({ ...collateral, ...collateralMeta });
      } else {
        return productImageUtil.create(collateral);
      }
    },
    createProductImages(items) {
      this.$store.dispatch('productImage/createItems', items).then(() => {
        this.$emit('complete', {
          event: this.$store.state.productImage.items,
          mode: this.selectableMode,
        });
      }).catch(() => {
        console.log('ERROR CREATING ITEM');
      });
    },

    onAddImage() {
      this.selected.forEach((obj) => {
        const data = {
          source: this.selectableMode,
          location: this.location,
          image_url: obj.image_url,
          uploader_profile: obj.uploader_profile,
          resource_url: obj.url,
          product_id: obj?.product?.pk,
          product_url: obj?.product?.url,
        };

        sendToMixPanel(MIXPANEL_EVENTS.addImage, data);
      });

      if (this.selectableMode === UPLOAD_MODE.CATALOG) {
        this.$emit('complete', {
          event: this.selected,
          mode: this.selectableMode,
        });
        return;
      }

      if (this.useStore && !this.parsingRequired) {
        this.createProductImages(this.selected);
        return;
      } else if (this.parsingRequired) {
        // see in backend `Collateral.meta` - a CharField that holds a JSON string
        /**
         * When adding products from an album, it can go one of 3 ways (in the order they're executed below):
         * 1. Adding a single collateral with metadata, in which case it redirects you to fetch mode.
         * 2. Adding multiple collaterals, with or without metadata, in which case they're all created in bulk.
         * 3. Adding a single collateral without metadata, in which case the ProductEditor is opened (see
         * onSelectorComplete(event) in ProductRowComponent.vue)
         */
        const shouldIGotoFetchURL = !this.disableUrlUpload;
        if (this.selected.length === 1 && this.hasMetadata(this.selected[0]) && shouldIGotoFetchURL) {
          const collateralMeta = JSON.parse(this.selected[0].meta) || {};
          this.changeTab(0);
          this.$refs.productSelectorFetch.urlToFetch = collateralMeta.site_url;
          this.$refs.productSelectorFetch.displayTitle = true;
          return;
        } else if (this.useStore && this.selected.length > 1) {
          const parsedCollaterals = this.selected.map((item) => this.parseCollateralToProductImage(item));
          this.createProductImages(parsedCollaterals);
          return;
        } else if (this.selected.length > 1 && this.selected[0].meta) {
          // Convert a Collateral object into a product object so that the backend can create the corresponding product
          const transform_item = (item) => {
            const meta = JSON.parse(item.meta);
            const prod = {
              brand: { name: meta.brand },
              title: meta.description,
              external_id: meta.external_id,
              site_url: meta.site_url,
            };
            return prod;
          };
          const parsedCollaterals = this.selected.map((item) => ({ site_url: JSON.parse(item.meta).site_url, image: item.image_url, product: { ...transform_item(item), ...{ images: [{ image_url: item.image_url }] } } }));
          this.$emit('complete', {
            event: parsedCollaterals,
            mode: this.selectableMode,
          });
          return;
        }
      }
      this.$emit('complete', {
        event: this.selected,
        mode: this.selectableMode,
      });
    },
    onLoadMoreImages() {
      this.loadMore();
    },
    // eslint-disable-next-line
    onSearch: _.debounce(function (evt) {
      const { search, folder } = evt;
      this.hasFolderParam(folder);
      this.search = search;
    }, 200),
  },
};
</script>
<style lang="scss">
.modal-dialog{
  justify-content: center;
}
.product__thumb {
  min-width: 100px;
  width: 100px;
  height: 100px;
  margin: 1rem;
  cursor: pointer;
  position: relative;

  &--selected {
    box-shadow: 0 0 0 2px $primary;
  }

  &__icon {
    display: block;
    width: 20px;
    height: 20px;
    position: absolute;
    background-color: $primary;
    top: 5px;
    right: 5px;
    border-radius: 20px;
    color: white;
    font-size: 0.75rem;
    text-align: center;
    padding: 0.2rem;
  }

  &__img {
    max-width: 100%;
    width: 100%;
    object-fit: contain;
    pointer-events: none;
    user-select: none;
  }
}

.heading {
  padding: 1rem;
  background-color: #ffffff;
  margin-left: -1rem;
  margin-top: -1rem;
  margin-right: -1rem;
  border-radius: 4px;
  padding-bottom: 4px;
}

.tab-fixes .base-tabs__item {
  background-color: transparent !important;
  margin-left: 0;
  padding-left: 0;

  &--underlined {
    margin-right: 18px;
    padding: 0 !important;
    &.active {
      font-family: $font-family-sans-serif-semibold;
      color: $black;
      border-bottom: 2px #e7873c solid;
    }
  }
}

.bottom-line {
  border-bottom: 1px #d4d4d4 solid;
}

.modal-footer {
  padding: 0px 0.75rem 0.75rem 0px;
  border: 0px none transparent !important;
}
</style>
<style lang="scss" scoped>
.add-image-button {
  // margin-right: 26px !important;
  padding: 9px 16px;
  font-size: 14px;
}
::v-deep .base-button {
  // width: 140px !important;
  white-space: nowrap;
}

::v-deep .modal-body {
  padding-bottom: 0;
  max-height: 740px;
}
::v-deep .modal-content{
  .close{
    right: 25px !important;
    top: 30px !important;
    padding: unset !important;
    opacity: 0.6 !important;
  }
}
::v-deep .modal-dialog{
  width: 87.5vw;
  max-width: 1120px;
  @media (max-width: 576px){
    max-width: 91% !important;
    max-height: 75% !important;
  }
}
::v-deep .grid-thumb{
    max-height: 345px;
  }

.keyboardUp{
  ::v-deep .modal-body {
    padding-bottom: 0;
    max-height: unset !important;
  }
  ::v-deep .grid-thumb{
    min-height: 514px;
  }
  ::v-deep .image_url{
    margin: 28% 0px 28% 0px;
  }
  ::v-deep .modal-body {
    height: 740px !important;
    max-height: 740px !important;
    @media (max-width: 570px){
      margin-bottom: 40%;
    }
}
}
</style>
